import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {RoadKill} from "../RoadKill";
import {TimelineLite, TimelineMax, gsap} from "gsap";
import {SimpleReelModelEventData} from "../../core/reel/simplereel/SimpleReelModel";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {ArrayUtils} from "../utils/ArrayUtils";
import {
    CAR_NAMES,
    JumpingWildAnimStates,
    JW_TYPE,
    RoadKillSymbolStates,
    SimpleReelModelState,
    SYMBOL_NAME
} from "../component/enums/RoadKillEnums";
import {RoadKillGameConfig} from "../RoadKillGameConfig";
import {RoadKillSymbol} from "../reels/RoadKillSymbol";
import {JumpingWild} from "../component/JumpingWild";
import {RoadKillUtils} from "../utils/RoadKillUtils";
import {Split} from "../component/Split";
import {RoadKillModel} from "../server/RoadKillModel";
import {RoadKillSound} from "../sound/RoadKillSound";
import {ITrappedCarInfo} from "../server/RoadKillData";
import {RoadKillGameMode} from "../server/RoadKillGameMode";
import {RKHelper} from "../utils/RKHelper";
import {SimplePresentationRunnerController} from "../../core/winpresentation/SimplePresentationRunner";

export class RoadKillNormalResultPresentation implements FlowStatePresentation {
    private _tl: TimelineLite | undefined;
    private _stoppedReels: boolean[];

    public start(flowStateController: FlowStateController) {

        this._stoppedReels = [false, false, false, false, false];

        this._tl = new TimelineMax();
        let stopTime: number = 0;
        const stopDelay: number = RoadKill.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? RoadKillGameConfig.REEL_STOP_FAST_SPIN_DELAY :
            RoadKillGameConfig.REEL_STOP_DELAY;
        if (RoadKill.model.gameData.wasFeatureBuy && RoadKill.model.gameData.mode === RoadKillGameMode.NORMAL && RoadKill.model.gameData.nextMode === RoadKillGameMode.SUPER_FREESPIN) {
            const animationName: string = RKHelper.getTransitionAnimName(RoadKill.model.gameData.mode, RoadKill.model.gameData.nextMode);
            this._tl.add(RoadKill.backgroundElements.transitionNormalToSFS(animationName));
            stopTime = 2.10;
            this._tl.add(() => {
                flowStateController.setSkipCallback(() => this.skip()); //only for test
            });
        } else {
            flowStateController.setSkipCallback(() => this.skip());
        }


        for (let i: number = 0; i < RoadKill.model.gameData.reels.length; i++) {
            const reelData: string[] = RoadKill.model.gameData.reels[i];
            const reelStopAnimation: TimelineMax = new TimelineMax();

            reelStopAnimation.add(this.preventSkipOnLastReel(i, flowStateController));
            reelStopAnimation.add(RoadKill.reelView.reelModels[i].stop(reelData));

            RoadKill.reelView.reelModels[i].events?.once(SimpleReelModelState.STOPPED, (data: SimpleReelModelEventData) => this.onReelStop(data, flowStateController));
            RoadKill.reelView.reelModels[i].events?.once(SimpleReelModelState.BOUNCE, (data: SimpleReelModelEventData) => this.onReelBounce(data));

            this._tl.add(reelStopAnimation, stopTime);
            stopTime += stopDelay;
        }


        const padding: number = RoadKill.gameFlowController.calculatePaddingDuration(this._tl.totalDuration());
        if (padding > 0) {
            //Logger.logDev("Added minimum jurisdiction padding  ", padding);
            //Logger.logDev("Total before padding:"+this._tl.totalDuration());
            this._tl.shiftChildren(padding);
            //Logger.logDev("Total after padding:"+this._tl.totalDuration());
        }


    }

    public skip(): void {
        if (ArrayUtils.isAllTrue(this._stoppedReels)) {
            console.warn("Invalid skip called");
            return;
        }
        this._tl?.kill();
        this._tl = new TimelineMax();
        const stopDelay = RoadKill.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? RoadKillGameConfig.STEP_DURATION * 0.75 :
            RoadKillGameConfig.STEP_DURATION * 2.5;
        let stopTime = 0;
        for (let i: number = 0; i < RoadKill.model.gameData.reels.length; i++) {
            const reelModel = RoadKill.reelView.reelModels[i];
            if (reelModel.state != SimpleReelModelState.SPIN) {
                continue;
            }

            const reelData = RoadKill.model.gameData.reels[i];
            const reelStopAnimation = new TimelineMax();
            reelStopAnimation.add(RoadKill.reelView.reelModels[i].stop(reelData));

            this._tl.add(reelStopAnimation, stopTime);
            stopTime += stopDelay;
        }


    }

    private preventSkipOnLastReel(reelId: number, flowStateController: FlowStateController) {
        const tl: TimelineLite = new TimelineLite();
        if (reelId == 4) {
            tl.add(() => {
                flowStateController.setSkipCallback();
            }); //prevents useless skips when last reel has started its stop anyway.
        }
        return tl;
    }

    private onReelBounce(data: SimpleReelModelEventData) {
        RoadKill.jumpingWildView.getWilds().forEach((jw: JumpingWild) => {
            if (jw.visible && jw.animationState === JumpingWildAnimStates.IN && jw.type !== JW_TYPE.RABBIT) {
                jw.playOutAnim();
            }
        })
    }


    private onReelStop(data: SimpleReelModelEventData, flowStateController: FlowStateController) {
        this._stoppedReels[data.reel] = true;
        RoadKill.reelView.removeExcitement(data.reel);
        const tl: TimelineLite = new TimelineLite(),
            gameModel: RoadKillModel = RoadKill.model;
        let scatterLandSoundPlayOnce: boolean = true;
        for (let symIdx: number = 0; symIdx < gameModel.evaluatedArea[0].length; symIdx++) {
            const sym: RoadKillSymbol = RoadKill.reelView.getVisibleSymbolAt(data.reel, symIdx);
            if (sym.symbolName === SYMBOL_NAME.SCATTER) {
                tl.add([sym.setState(RoadKillSymbolStates.LAND, ""), () => {
                    if (scatterLandSoundPlayOnce) {
                        RoadKill.soundPlayer.playTrack(RoadKillSound.SCATTER_LAND);
                        scatterLandSoundPlayOnce = false;
                    }
                }], 0)
            }
            for (let trappedCarIdx: number = 0; trappedCarIdx < RoadKill.jumpingWildView.trappedCarPosition.length; trappedCarIdx++) {
                const trappedCar: ITrappedCarInfo = RoadKill.jumpingWildView.trappedCarPosition[trappedCarIdx];
                if ((trappedCar[CAR_NAMES.RED] && trappedCar[CAR_NAMES.RED][0] === data.reel && trappedCar[CAR_NAMES.RED][1]) === symIdx || (trappedCar[CAR_NAMES.GREEN] && trappedCar[CAR_NAMES.GREEN][0] === data.reel && trappedCar[CAR_NAMES.GREEN][1] === symIdx)) {
                    sym.createTrappedCar(trappedCar[CAR_NAMES.RED] ? CAR_NAMES.RED : CAR_NAMES.GREEN);
                    tl.to(sym.trappedCar, {alpha: 1, duration: 0.1}, 0);
                }
            }
        }
        tl.add(this.addSplits(data.reel));
        if (ArrayUtils.isAllTrue(this._stoppedReels)) {
            if (RoadKill.jumpingWildView.getOverlaySplitContainer().children.length > 0) {
                tl.add(() => {
                    RoadKill.soundPlayer.playTrack(RoadKillSound.SPLIT_SWOOSH);
                }, "<")
            }

            tl.add(() => {
                RoadKill.reelView.onReelStop();
            });
            tl.add(RoadKill.jumpingWildView.onReelStop());
            RoadKill.jumpingWildView.fadeOutTrappedCars();
            if (RoadKill.model.gameData.betWayWins.length > 0) {
                tl.add(() => this.addJWMultiplierAndWild());
                tl.add(this.addWilds());
            }

            if (RoadKill.model.isMassiveWin) {
                tl.addLabel("forMassiveWinWild")
                const reelId: number = RoadKillGameConfig.REELS_NUM - 1;
                for (let symId: number = 0; symId < RoadKill.model.gameData.evaluatedArea[0].length; symId++) {
                    if (RoadKill.model.gameData.evaluatedArea[reelId][symId] === SYMBOL_NAME.WILD) {
                        RoadKill.reelView.getVisibleSymbolAt(reelId, symId).createWildOverlay(reelId, symId);
                        tl.add(RoadKill.reelView.getVisibleSymbolAt(reelId, symId).playWildOverlayLandAnimation(), "forMassiveWinWild");
                    }
                }
            }

            tl.add(() => {
                this.completePresentation(flowStateController);
            })
        }
    }

    private addJWMultiplierAndWild(): void {
        RoadKill.jumpingWildView.getWilds().forEach((jw: JumpingWild) => {
            if (jw.visible && jw.reelPosition) {
                const symName: SYMBOL_NAME = RoadKill.model.evaluatedArea[jw.reelPosition.reelId][jw.reelPosition.rowId] as SYMBOL_NAME;
                if (RoadKillGameConfig.WILD_SYMBOLS.indexOf(symName) > -1) {
                    const sym: RoadKillSymbol = RoadKillUtils.getSymbol(jw.reelPosition.reelId, jw.reelPosition.rowId);
                    sym.changeSymbol(symName);
                    RoadKill.jumpingWildView.reelSymbolContainer.addChild(sym);
                    const symSize: number = RoadKill.model.data.symbolSizes[jw.reelPosition.reelId][jw.reelPosition.rowId];
                    if (symSize > 1) {
                        sym.addMultiplier(symSize);
                    }
                    sym.addToggleWild();
                }
            }
        });
    }

    private addSplits(reelId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const splitOverlayContainer: PIXI.Container = RoadKill.jumpingWildView.getOverlaySplitContainer();
        if (splitOverlayContainer.children.length > 0) {
            tl.addLabel("makeSplitSymPart")
            for (let i: number = 0; i < splitOverlayContainer.children.length; i++) {
                const split: Split = splitOverlayContainer.children[i] as Split;
                if (reelId === split.reelId) {
                    const sym: RoadKillSymbol = RoadKill.reelView.getVisibleSymbolAt(split.reelId, split.rowId);
                    if (!sym.multiplier) {
                        if (sym.symbolName !== RoadKill.model.evaluatedArea[split.reelId][split.rowId] && (RoadKill.model.evaluatedArea[split.reelId][split.rowId] !== "W")) {
                            sym.changeSymbol(RoadKill.model.evaluatedArea[split.reelId][split.rowId] as SYMBOL_NAME);
                        }
                        tl.add([RKHelper.playSpineWithTimeline(split, 0, "splitOut"), sym.glowPreHit(true), sym.setSplit(true)], "makeSplitSymPart");
                        tl.add(() => {
                            split.alpha = 0;
                        });
                    }
                }
            }
            if (reelId === 4) {
                tl.add(() => {
                    splitOverlayContainer.removeChildren();
                });
            }
        }
        return tl;
    }

    private addWilds(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const wildOverlayContainer: PIXI.Container = RoadKill.jumpingWildView.getOverlayWildContainer();
        if (wildOverlayContainer.children.length > 0) {
            tl.addLabel("makeWildSymPart")
            for (let i: number = 0; i < wildOverlayContainer.children.length; i++) {
                const individualWild: PIXI.DisplayObject = wildOverlayContainer.children[i],
                    reelId: number = Number(individualWild.name[0]),
                    symId: number = Number(individualWild.name[1]);
                const sym: RoadKillSymbol = RoadKill.reelView.getVisibleSymbolAt(reelId, symId);
                tl.add(() => sym.createWildOverlay(reelId, symId, true), "makeWildSymPart");
            }
            tl.add(() => {
                RoadKill.jumpingWildView.getOverlayWildContainer().removeChildren();
            })
        }
        return tl;
    }

    private completePresentation(flowStateController: FlowStateController) {
        let delay: number = 0;
        if (RoadKill.model.gameData?.betWayWins.length) {
            if (RoadKill.model.data.mode === RoadKillGameMode.NORMAL && RoadKill.model.data.nextMode === RoadKillGameMode.NORMAL) {
                delay = 0.2;
            }
        }
        gsap.delayedCall(delay, () => {
            flowStateController.finish();
            delete this._tl;
        });
    }
}